import * as R from 'ramda'
import {
  SapKey,
  EquipmentGroupStId,
  EquipmentCategory,
  Criticality,
  ExtraService,
  ExtraServiceEquipmentPrice,
  ClientFrontlineConfig,
  EquipmentBase,
  BaseExtraServiceEquipmentPrice,
  BaseExtraService,
  ExtraCommercialTerm,
  ExtraCommercialTermEquipmentPrice,
  BaseExtraServiceAnswer,
  BaseExtraServiceKey
} from '../../../../types/types'
import { EncodedVars, CommercialTerms, ExtraCommercialTerms, CommercialTermKey } from 'nemo-pricing'
import { arrayifySelections, getModules } from '../../../common/offer-selections'
import { Offering } from '../offering/types'
import { selections, isSelectionUnchecked } from '../offering/selections'
import {
  getEquipmentName,
  inflateFlatExtraServicePrices,
  inflateFlatExtraCommercialTermPrices
} from '../../../common/extra-services'
import { round } from '../../../common/prices'
import { Group } from '../assessmentResults/equipment'

export function isExtraServicePricesOk(extraServices?: ExtraService[], extraCommercialTerms?: ExtraCommercialTerm[]) {
  const isOk = (baseExtraServices?: BaseExtraService[]) => {
    if (!baseExtraServices) {
      return true
    }
    return baseExtraServices.filter(es => es.answers.filter(a => a.totalPrice <= 0).length > 0).length === 0
  }
  return isOk(extraServices) && isOk(extraCommercialTerms)
}

const possibleBaseExtraServices: Record<BaseExtraServiceKey, boolean> = {
  KCSM_AIR_CONDITION_MAINT: true,
  KCSM_CARE_INSPECTION: true,
  KCSM_CLEANING: true,
  KCSM_INSP_SCE_COST_COVERAGE: true,
  KCSM_INSP_SCE_TEST: true,
  KCSM_INSURANCE_COVERAGE: true,
  KCSM_PRESENCE: true,
  CNSM_LABOR_FEE: true,
  CNSM_COST_AGENT: true,
  CNSM_COST_SUBCON: true,
  CNSM_COST_PROJECT: true,
  CNSM_COST_BID: true,
  CNSM_COST_DEPOT: true,
  KCSM_SERVICE_ACCESS_PLATFORM: true,
  KCSM_VOICE_L: true,
  KCSM_EXTRA_FEE: true
}

function isBaseExtraServiceKey(x: string): x is BaseExtraServiceKey {
  return !!(possibleBaseExtraServices as Record<string, boolean>)[x]
}

function createAnswers(
  equipment: EquipmentBase[],
  answers: Array<{ key: BaseExtraServiceKey; answer: string }>,
  groupName: string
): BaseExtraServiceEquipmentPrice[] {
  return R.chain(
    e =>
      answers.map(a => ({
        key: a.key,
        answer: a.answer,
        equipmentId: e.salesToolId,
        equipmentGroup: e.groupId,
        equipmentName: getEquipmentName(e),
        equipmentType: e.equipmentType,
        koneEquipmentNumber: e.koneEquipmentNumber,
        groupName,
        servicePrice: 0,
        isCustomPrice: false
      })),
    equipment
  )
}

function createEquipmentAnswers(
  equipment: EquipmentBase[],
  answers: Array<{ key: BaseExtraServiceKey; answer: string }>,
  groupName: string
): ExtraServiceEquipmentPrice[] {
  return createAnswers(equipment, answers, groupName) as ExtraServiceEquipmentPrice[]
}

function createCommercialTermAnswers(
  equipment: EquipmentBase[],
  answers: Array<{ key: BaseExtraServiceKey; answer: string }>,
  groupName: string
): ExtraCommercialTermEquipmentPrice[] {
  return createAnswers(equipment, answers, groupName) as ExtraCommercialTermEquipmentPrice[]
}

function extractGroupEquipmentByType(
  equipmentInGroup: EquipmentBase[],
  equipmentCategory: EquipmentCategory,
  criticality: Criticality
) {
  return equipmentInGroup.filter(v => v.criticality === criticality && v.equipmentCategory === equipmentCategory)
}

function filterExtraServices(
  xs: Array<{ key: SapKey | CommercialTermKey; answer: string }>,
  possibleExtraServices: Array<SapKey | CommercialTermKey>
): Array<{ key: BaseExtraServiceKey; answer: string }> {
  return xs.filter(x => isBaseExtraServiceKey(x.key) && possibleExtraServices.includes(x.key)) as Array<{
    key: BaseExtraServiceKey
    answer: string
  }>
}

export function extractServices(
  offering: Offering,
  groups: Group[],
  frontlineConfig: ClientFrontlineConfig
): ExtraService[] {
  if (offering._tag === 'Flexible') {
    if (!frontlineConfig.offerSelections) {
      return []
    }

    const elevatorServices = getModules(frontlineConfig.offerSelections.group.elevator).find(
      v => v.module === 'extra_services'
    )
    const escalatorServices = getModules(frontlineConfig.offerSelections.group.escalator).find(
      v => v.module === 'extra_services'
    )
    const doorServices = getModules(frontlineConfig.offerSelections.group.door).find(v => v.module === 'extra_services')

    const resiflowServices = getModules(frontlineConfig.offerSelections.group.resiflow).find(
      v => v.module === 'extra_services'
    )

    const extraServiceKeys = R.uniq(
      (elevatorServices ? elevatorServices.items : [])
        .concat(escalatorServices ? escalatorServices.items : [])
        .concat(doorServices ? doorServices.items : [])
        .concat(resiflowServices ? resiflowServices.items : [])
    )

    function extractGroupAnswers(
      groupId: EquipmentGroupStId,
      equipmentCategory: EquipmentCategory,
      criticality: Criticality
    ) {
      const s = selections({ groupId, equipmentCategory, criticality }, offering)
      return filterExtraServices(
        s.fold(
          () => [],
          xs =>
            R.chain(
              ([key, groupSelections]: [SapKey, string | string[] | null | undefined]) =>
                R.chain(
                  answer => (isSelectionUnchecked(answer) ? [] : [{ key, answer }]),
                  arrayifySelections(groupSelections)
                ),
              R.toPairs(xs)
            )
        ),
        extraServiceKeys
      )
    }

    // Extract everything to long EquipmentPrice[]
    const prices = R.flatten(
      groups.map(group =>
        R.flatten([
          createEquipmentAnswers(
            extractGroupEquipmentByType(group.equipment, 'elevator', 'normal'),
            extractGroupAnswers(group.groupId, 'elevator', 'normal'),
            group.name
          ),
          createEquipmentAnswers(
            extractGroupEquipmentByType(group.equipment, 'elevator', 'critical'),
            extractGroupAnswers(group.groupId, 'elevator', 'critical'),
            group.name
          ),
          createEquipmentAnswers(
            extractGroupEquipmentByType(group.equipment, 'escalator', 'normal'),
            extractGroupAnswers(group.groupId, 'escalator', 'normal'),
            group.name
          ),
          createEquipmentAnswers(
            extractGroupEquipmentByType(group.equipment, 'escalator', 'critical'),
            extractGroupAnswers(group.groupId, 'escalator', 'critical'),
            group.name
          ),
          createEquipmentAnswers(
            extractGroupEquipmentByType(group.equipment, 'door', 'normal'),
            extractGroupAnswers(group.groupId, 'door', 'normal'),
            group.name
          ),
          createEquipmentAnswers(
            extractGroupEquipmentByType(group.equipment, 'door', 'critical'),
            extractGroupAnswers(group.groupId, 'door', 'critical'),
            group.name
          ),
          createEquipmentAnswers(
            extractGroupEquipmentByType(group.equipment, 'resiflow', 'normal'),
            extractGroupAnswers(group.groupId, 'resiflow', 'normal'),
            group.name
          )
        ])
      )
    )
    return inflateFlatExtraServicePrices(prices)
  }
  return []
}

export const possibleExtraCommercialTerms: Record<keyof ExtraCommercialTerms, boolean> = {
  CNSM_COST_AGENT: true,
  CNSM_COST_SUBCON: true,
  CNSM_COST_PROJECT: true,
  CNSM_COST_BID: true,
  CNSM_COST_DEPOT: true
}

export const isExtraCommercialTerm = (key: string) => !!(possibleExtraCommercialTerms as Record<string, boolean>)[key]

export function extractCommercialTerms(
  offering: Offering,
  commercialTerms: CommercialTerms,
  groups: Group[]
): ExtraCommercialTerm[] {
  const commercialTermsPairs = R.toPairs<CommercialTerms>(commercialTerms).filter(
    ([_, value]) => typeof value === 'string' || value === undefined
  ) as [keyof CommercialTerms, string | undefined][]
  if (offering._tag === 'Flexible') {
    const selectedTerms = filterExtraServices(
      R.chain(([key, answer]: [CommercialTermKey, string | undefined]) => {
        if (answer && !isSelectionUnchecked(answer)) {
          return [{ key, answer }]
        }
        return []
      }, commercialTermsPairs),
      R.keys(possibleExtraCommercialTerms)
    )

    const prices = R.flatten(
      groups.map(group =>
        R.flatten([
          createCommercialTermAnswers(
            extractGroupEquipmentByType(group.equipment, 'elevator', 'normal'),
            selectedTerms,
            group.name
          ),
          createCommercialTermAnswers(
            extractGroupEquipmentByType(group.equipment, 'elevator', 'critical'),
            selectedTerms,
            group.name
          ),
          createCommercialTermAnswers(
            extractGroupEquipmentByType(group.equipment, 'escalator', 'normal'),
            selectedTerms,
            group.name
          ),
          createCommercialTermAnswers(
            extractGroupEquipmentByType(group.equipment, 'escalator', 'critical'),
            selectedTerms,
            group.name
          ),
          createCommercialTermAnswers(
            extractGroupEquipmentByType(group.equipment, 'door', 'normal'),
            selectedTerms,
            group.name
          ),
          createCommercialTermAnswers(
            extractGroupEquipmentByType(group.equipment, 'door', 'critical'),
            selectedTerms,
            group.name
          ),
          createCommercialTermAnswers(
            extractGroupEquipmentByType(group.equipment, 'resiflow', 'normal'),
            selectedTerms,
            group.name
          )
        ])
      )
    )
    return inflateFlatExtraCommercialTermPrices(prices) as ExtraCommercialTerm[]
  }
  return []
}

export function fillPricesForExtraServices(newExtraServices: ExtraService[], oldExtraServices: ExtraService[]) {
  return fillPricesForBaseExtraServices(newExtraServices, oldExtraServices) as ExtraService[]
}

export function fillPricesForExtraCommercialTerms(
  newExtraCommercialTerms: ExtraCommercialTerm[],
  oldExtraCommercialTerms: ExtraCommercialTerm[]
) {
  return fillPricesForBaseExtraServices(newExtraCommercialTerms, oldExtraCommercialTerms) as ExtraCommercialTerm[]
}

function fillPricesForBaseExtraServices(newExtraServices: BaseExtraService[], oldExtraServices: BaseExtraService[]) {
  const getExtraService = (extraServices: BaseExtraService[], defaultExtraService: BaseExtraService) =>
    extraServices.find(oes => oes.key === defaultExtraService.key) || defaultExtraService
  const getAnswer = (es: BaseExtraService, defaultAnswer: BaseExtraServiceAnswer) =>
    es.answers.find(a => a.answer === defaultAnswer.answer) || defaultAnswer
  const getPrice = (answer: BaseExtraServiceAnswer, defaultPrice: BaseExtraServiceEquipmentPrice) =>
    answer.equipmentPrices.find(p => p.equipmentId === defaultPrice.equipmentId) || defaultPrice
  const equipmentMatch = (a: BaseExtraServiceEquipmentPrice[], b: BaseExtraServiceEquipmentPrice[]) =>
    a.length !== b.length
      ? false
      : a.every(va => b.some(vb => va.equipmentGroup === vb.equipmentGroup && va.equipmentId === vb.equipmentId))

  return newExtraServices.map(emptyService => {
    const filledService = getExtraService(oldExtraServices, emptyService)
    return {
      ...emptyService,
      answers: emptyService.answers.map(emptyAnswer => {
        const filledAnswer = getAnswer(filledService, emptyAnswer)
        const sameEquipment = equipmentMatch(filledAnswer.equipmentPrices, emptyAnswer.equipmentPrices)
        return {
          ...filledAnswer,
          totalPrice: sameEquipment ? filledAnswer.totalPrice : 0,
          equipmentPrices: sameEquipment
            ? emptyAnswer.equipmentPrices.map(emptyPrice => getPrice(filledAnswer, emptyPrice))
            : emptyAnswer.equipmentPrices
        }
      })
    }
  })
}

export const calculateTotalExtraServicePrice = (services: BaseExtraService[] | undefined) =>
  services ? round(R.sum(R.flatten(services.map(s => s.answers.map(a => a.totalPrice))))) : 0

export function getDefaultPricesForEquipmentServices(
  equipmentPrices: ExtraServiceEquipmentPrice[],
  totalPrice: number,
  resetCustomPrices: boolean = false
): ExtraServiceEquipmentPrice[] {
  return getDefaultPricesForEquipment(equipmentPrices, totalPrice, resetCustomPrices) as ExtraServiceEquipmentPrice[]
}

export function getDefaultPricesForEquipmentCommercialTerms(
  equipmentPrices: ExtraCommercialTermEquipmentPrice[],
  totalPrice: number,
  resetCustomPrices: boolean = false
): ExtraCommercialTermEquipmentPrice[] {
  return getDefaultPricesForEquipment(
    equipmentPrices,
    totalPrice,
    resetCustomPrices
  ) as ExtraCommercialTermEquipmentPrice[]
}

function getDefaultPricesForEquipment(
  equipmentPrices: BaseExtraServiceEquipmentPrice[],
  totalPrice: number,
  resetCustomPrices: boolean = false
): BaseExtraServiceEquipmentPrice[] {
  const prices = equipmentPrices.map(x => ({ servicePrice: x.servicePrice, isCustomPrice: x.isCustomPrice }))
  return R.zip(getDefaultPrices(prices, totalPrice, resetCustomPrices), equipmentPrices).map(([updated, existing]) => ({
    ...existing,
    servicePrice: updated.servicePrice,
    isCustomPrice: updated.isCustomPrice
  }))
}

interface Price {
  isCustomPrice: boolean
  servicePrice: number
}

export function getDefaultPrices(
  equipmentPrices: Price[],
  totalPrice: number,
  resetCustomPrices: boolean = false
): Price[] {
  // Reset custom prices if needed
  const resetPrices = resetCustomPrices ? equipmentPrices.map(ep => ({ ...ep, isCustomPrice: false })) : equipmentPrices
  const defaultPrice = calculateDefaultEquipmentPrice(totalPrice, resetPrices)
  // first apply the defaults
  const dividedDefaults = resetPrices.map(ep => ({
    ...ep,
    servicePrice: ep.isCustomPrice ? ep.servicePrice : defaultPrice
  }))
  const totalPriceRemaining = totalPrice - R.sum(dividedDefaults.map(x => x.servicePrice))
  // now divide the remainder among the non-custom equipment prices
  return dividedDefaults.reduce(
    ({ remaining, prices }: { remaining: number; prices: Price[] }, next: Price) => {
      const n = {
        ...next,
        servicePrice: !next.isCustomPrice ? round(next.servicePrice + remaining) : next.servicePrice
      }
      return {
        remaining: !next.isCustomPrice ? 0 : remaining,
        prices: prices.concat([n])
      }
    },
    { remaining: round(totalPriceRemaining), prices: [] }
  ).prices
}

// Default price is floored and doesn't always add up to total
function calculateDefaultEquipmentPrice(serviceTotal: number, equipmentPrices: Price[]) {
  const totalEquipment = equipmentPrices.length
  const overriddenEquipment = equipmentPrices.filter(ep => ep.isCustomPrice)
  const overridenPrices = overriddenEquipment.reduce((acc, ep) => acc + (ep.isCustomPrice ? ep.servicePrice : 0), 0)
  const leftToBeDivided = serviceTotal - overridenPrices
  const divider = totalEquipment - overriddenEquipment.length
  const defaultPrice = divider > 0 ? leftToBeDivided / divider : 0
  return round(defaultPrice)
}

const findExtraServicePriceMultiple = (
  equipmentId: string,
  extraServices: ReadonlyArray<BaseExtraService>,
  key: keyof EncodedVars.ExtraServicePrices
) => {
  const service = extraServices.find(e => e.key === key)
  const answers = (
    service != null
      ? service.answers.map(a => a.equipmentPrices.find(e => e.equipmentId === equipmentId)).filter(a => a != null)
      : []
  ) as BaseExtraServiceEquipmentPrice[]
  // Pricing module expects monthly prices but users supply yearly prices
  return answers.map((a): [string, number] => [a.answer, a.servicePrice / 12.0])
}

const findExtraServicePriceSingle = (
  equipmentId: string,
  extraServices: ReadonlyArray<BaseExtraService>,
  key: keyof EncodedVars.ExtraServicePrices
) => {
  const answers = findExtraServicePriceMultiple(equipmentId, extraServices, key)
  return answers.length > 0 ? answers[0][1] : 0
}

export const getExtraServicePrices = (
  equipmentId: string,
  extraServices?: ReadonlyArray<ExtraService>,
  extraCommercialTerms?: ReadonlyArray<ExtraCommercialTerm>
): EncodedVars.ExtraServicePrices => ({
  CNSM_LABOR_FEE: extraServices ? findExtraServicePriceSingle(equipmentId, extraServices, 'CNSM_LABOR_FEE') : 0,
  KCSM_AIR_CONDITION_MAINT: extraServices
    ? findExtraServicePriceSingle(equipmentId, extraServices, 'KCSM_AIR_CONDITION_MAINT')
    : 0,
  KCSM_INSURANCE_COVERAGE: extraServices
    ? findExtraServicePriceSingle(equipmentId, extraServices, 'KCSM_INSURANCE_COVERAGE')
    : 0,
  KCSM_PRESENCE: extraServices ? findExtraServicePriceSingle(equipmentId, extraServices, 'KCSM_PRESENCE') : 0,
  KCSM_CLEANING: extraServices ? findExtraServicePriceMultiple(equipmentId, extraServices, 'KCSM_CLEANING') : [],
  KCSM_CARE_INSPECTION: extraServices
    ? findExtraServicePriceSingle(equipmentId, extraServices, 'KCSM_CARE_INSPECTION')
    : 0,
  KCSM_INSP_SCE_COST_COVERAGE: extraServices
    ? findExtraServicePriceSingle(equipmentId, extraServices, 'KCSM_INSP_SCE_COST_COVERAGE')
    : 0,
  KCSM_INSP_SCE_TEST: extraServices
    ? findExtraServicePriceMultiple(equipmentId, extraServices, 'KCSM_INSP_SCE_TEST')
    : [],
  KCSM_SERVICE_ACCESS_PLATFORM: extraServices
    ? findExtraServicePriceSingle(equipmentId, extraServices, 'KCSM_SERVICE_ACCESS_PLATFORM')
    : 0,
  CNSM_COST_AGENT: extraCommercialTerms
    ? findExtraServicePriceSingle(equipmentId, extraCommercialTerms, 'CNSM_COST_AGENT')
    : 0,
  CNSM_COST_BID: extraCommercialTerms
    ? findExtraServicePriceSingle(equipmentId, extraCommercialTerms, 'CNSM_COST_BID')
    : 0,
  CNSM_COST_DEPOT: extraCommercialTerms
    ? findExtraServicePriceSingle(equipmentId, extraCommercialTerms, 'CNSM_COST_DEPOT')
    : 0,
  CNSM_COST_PROJECT: extraCommercialTerms
    ? findExtraServicePriceSingle(equipmentId, extraCommercialTerms, 'CNSM_COST_PROJECT')
    : 0,
  CNSM_COST_SUBCON: extraCommercialTerms
    ? findExtraServicePriceSingle(equipmentId, extraCommercialTerms, 'CNSM_COST_SUBCON')
    : 0,
  KCSM_VOICE_L: extraServices ? findExtraServicePriceSingle(equipmentId, extraServices, 'KCSM_VOICE_L') : 0,
  KCSM_EXTRA_FEE: extraServices ? findExtraServicePriceSingle(equipmentId, extraServices, 'KCSM_EXTRA_FEE') : 0
})
